/**
 * Chat — the slide-up log + input row (spec:
 * docs/superpowers/specs/2026-07-18-social-systems-design.md §2/§6/§8).
 *
 * Non-modal: the log idles translucent in the bottom-right corner and never
 * takes the pointer; ENTER (bound in main) opens + focuses the input row, which is
 * the ONLY live-gameplay text entry in the game. While it is focused:
 *   - the Input typing gate silences sim sampling (engine/input.ts), and
 *   - THIS element stops keydown/keyup propagation so main's window-bubble
 *     hotkeys (KeyJ/KeyB/…) never fire off typed letters. The event still
 *     reaches the field first (bubble listeners run after the target types).
 *
 * SECURITY INVARIANT: chat text is arbitrary player Unicode — every string
 * renders via textContent, NEVER innerHTML interpolation (names elsewhere are
 * alphabet-restricted; chat is not).
 */
import { CHAT_MAX, type ChatChannel, type ChatMsg } from "@hyperlight/shared";
import { strand } from "./meridian";

const LOG_CAP = 200;
const IDLE_FADE_MS = 6000;
/** Tab cycles through these; party/guild/direct answer server notices until
 *  their phases land, which is honest UX (the channel exists, it's empty). */
const CYCLE: ChatChannel[] = ["local", "party", "guild", "direct"];
const CH_LABEL: Record<ChatChannel, string> = {
  local: "LOCAL", party: "PARTY", guild: "GUILD", direct: "WHISPER",
};
/** One strand of the title line per channel — the same colours the social
 *  panel's tabs wear, so a party line and the PARTY tab read as one thing. */
const CH_COLOR: Record<ChatChannel, string> = {
  local: strand("local"), party: strand("party"), guild: strand("guild"), direct: strand("whisper"),
};

export class ChatPanel {
  /** Input row visible + focused — the Input typing gate is active. */
  focused = false;
  private root: HTMLElement;
  private logEl: HTMLElement;
  private row: HTMLElement;
  private chip: HTMLElement;
  private field: HTMLInputElement;
  private channel: ChatChannel = "local";
  /** Last outgoing whisper target — a bare message on WHISPER reuses it. */
  private lastTo: string | null = null;
  /** Last pilot who whispered me — `/r` answers them. */
  private lastFrom: string | null = null;
  private fadeTimer: number | null = null;
  /** History lines already rendered — warps can replay a room's scrollback. */
  private seenHist = new Set<string>();

  constructor(
    private handlers: {
      onSend: (m: { ch: ChatChannel; text: string; to?: string }) => void;
      /** true → caller released pointer lock; false → caller re-locks. */
      onFocusChange: (focused: boolean) => void;
      /** This pilot's own name — the ECHO of my own whisper carries `from: me`,
       *  and without this `/r` would happily answer myself. */
      self?: () => string | null;
    },
  ) {
    this.root = document.createElement("div");
    this.root.id = "chat-panel";
    // Bottom-RIGHT: clear of #hud-flight (bottom-left) and the minimap
    // (top-right); the right drawer overlays at z 30 while open, which is fine.
    this.root.style.cssText = [
      "position:fixed", "right:0", "bottom:132px", "width:430px", "z-index:8",
      "display:flex", "flex-direction:column", "pointer-events:none",
    ].join(";");

    this.logEl = document.createElement("div");
    this.logEl.className = "chat-log";
    this.logEl.style.cssText = [
      "max-height:186px", "overflow-y:auto", "word-break:break-word",
      "white-space:pre-wrap", "opacity:0.2", "transition:opacity 0.6s",
      "scrollbar-width:thin",
    ].join(";");
    this.root.appendChild(this.logEl);

    this.row = document.createElement("div");
    this.row.className = "chat-row";
    this.row.style.display = "none";
    this.chip = document.createElement("span");
    this.chip.className = "chat-chip";
    this.chip.title = "Tab cycles the channel";
    this.field = document.createElement("input");
    this.field.className = "chat-field";
    this.field.type = "text";
    this.field.maxLength = CHAT_MAX;
    this.field.autocomplete = "off";
    this.field.spellcheck = false;
    this.field.placeholder = "/w name · /r reply · /p /g /l";
    this.row.appendChild(this.chip);
    this.row.appendChild(this.field);
    this.root.appendChild(this.row);
    document.body.appendChild(this.root);
    this.setChannel("local");

    // Click a name in the log to whisper them — the fastest path from
    // "someone said something" to answering them privately.
    this.logEl.addEventListener("click", (e) => {
      const who = (e.target as HTMLElement).closest("[data-who]") as HTMLElement | null;
      const name = who?.dataset.who;
      if (name) this.prefillWhisper(name);
    });

    // Bubble-phase on the FIELD: the browser has already delivered the key to
    // the input (typing works), and stopping here keeps every window-bubble
    // hotkey in main.ts blind while chat is focused. Esc/Enter/Tab are ours.
    this.field.addEventListener("keydown", (e) => {
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        e.preventDefault();
        this.submit();
      } else if (e.code === "Escape") {
        e.preventDefault();
        this.closeInput(); // draft is kept — Esc hides, it doesn't discard
      } else if (e.code === "Tab") {
        e.preventDefault();
        this.setChannel(CYCLE[(CYCLE.indexOf(this.channel) + 1) % CYCLE.length]);
      }
      e.stopPropagation();
    });
    this.field.addEventListener("keyup", (e) => e.stopPropagation());
    // Losing focus any other way (a click on the canvas) closes the row too,
    // so `focused` can never disagree with where the keyboard actually is.
    this.field.addEventListener("blur", () => {
      if (this.focused) this.closeInput(false);
    });
  }

  /** Recent room scrollback (join/warp). Rendered dimmed — it's history, not
   *  live speech — and deduped so warping back into a room can't double it. */
  history(lines: ChatMsg[]): void {
    for (const msg of lines) {
      if (msg.err || msg.from === "") continue; // notices never belong to history
      const key = `${msg.at} ${msg.from} ${msg.text}`;
      if (this.seenHist.has(key)) continue;
      this.seenHist.add(key);
      this.append(msg, true);
    }
    // The set only ever holds what the server keeps per room (50), times the
    // rooms visited this session — trim the oldest half if it somehow balloons.
    if (this.seenHist.size > 800) {
      let drop = this.seenHist.size / 2;
      for (const k of this.seenHist) {
        if (drop-- <= 0) break;
        this.seenHist.delete(k);
      }
    }
  }

  /** A relayed line (or server notice) from the wire. */
  append(msg: ChatMsg, dim = false): void {
    const line = document.createElement("div");
    line.className = "chat-line";
    if (dim) line.style.opacity = "0.5";
    if (msg.err || msg.from === "") {
      line.classList.add("chat-note");
      line.textContent = msg.text;
    } else {
      const hue = CH_COLOR[msg.ch] ?? CH_COLOR.local;
      const ch = document.createElement("span");
      ch.className = "chat-ch";
      ch.style.color = hue;
      ch.textContent = `${CH_LABEL[msg.ch] ?? "?"} `;
      const who = document.createElement("span");
      who.className = "chat-who";
      who.style.color = hue;
      who.dataset.who = msg.from; // click → whisper them
      who.textContent = msg.ch === "direct" && msg.to ? `${msg.from} → ${msg.to} ` : `${msg.from} `;
      const body = document.createElement("span");
      body.textContent = msg.text; // hostile free text — textContent ONLY
      line.append(ch, who, body);
      // Remember who last whispered ME — /r answers them. My own echo of an
      // outgoing whisper looks identical apart from `from`, so skip it.
      if (msg.ch === "direct" && msg.to && msg.from && msg.from !== this.handlers.self?.()) {
        this.lastFrom = msg.from;
      }
    }
    this.logEl.appendChild(line);
    while (this.logEl.childElementCount > LOG_CAP) this.logEl.firstElementChild?.remove();
    this.logEl.scrollTop = this.logEl.scrollHeight;
    this.wake();
  }

  /** Local, client-only notice (usage errors, unknown commands). */
  note(text: string): void {
    this.append({ t: "chatMsg", ch: this.channel, from: "", text, at: 0, err: true });
  }

  /** Social-panel WHISPER button: arm the DIRECT channel at this target and
   *  focus the input — a bare Enter-send then goes straight to them. */
  prefillWhisper(name: string): void {
    this.setChannel("direct");
    this.lastTo = name;
    this.openInput();
  }

  openInput(): void {
    if (this.focused) return;
    this.focused = true;
    this.row.style.display = "flex";
    this.root.style.pointerEvents = "auto"; // scrollback becomes scrollable
    this.wake();
    this.handlers.onFocusChange(true);
    this.field.focus(); // focusin flushes held keys (Input typing gate)
  }

  closeInput(notify = true): void {
    if (!this.focused) return;
    this.focused = false;
    this.row.style.display = "none";
    this.root.style.pointerEvents = "none";
    this.field.blur();
    this.wake();
    if (notify) this.handlers.onFocusChange(false);
  }

  /** Probe/test hook: the rendered log lines as plain text. */
  probeLines(): string[] {
    return Array.from(this.logEl.children, (el) => el.textContent ?? "");
  }

  private setChannel(ch: ChatChannel): void {
    this.channel = ch;
    this.chip.textContent = CH_LABEL[ch];
    this.chip.style.color = CH_COLOR[ch];
    this.field.style.caretColor = CH_COLOR[ch];
  }

  private submit(): void {
    const raw = this.field.value.trim();
    if (raw.length === 0) {
      this.closeInput();
      return;
    }
    let ch = this.channel;
    let to: string | undefined;
    let text = raw;
    if (raw.startsWith("/")) {
      const space = raw.indexOf(" ");
      const cmd = (space === -1 ? raw : raw.slice(0, space)).toLowerCase();
      const rest = space === -1 ? "" : raw.slice(space + 1).trim();
      const chOf: Record<string, ChatChannel> = { "/l": "local", "/p": "party", "/g": "guild" };
      if (cmd in chOf) {
        this.setChannel(chOf[cmd]);
        if (rest.length === 0) return; // bare /p etc. just switches, row stays open
        ch = chOf[cmd];
        text = rest;
      } else if (cmd === "/r" || cmd === "/reply") {
        // Answer whoever spoke to you last — the classic MMO reflex.
        const target = this.lastFrom ?? this.lastTo;
        if (!target) {
          this.note("NOBODY HAS WHISPERED YOU");
          return;
        }
        if (rest.length === 0) {
          this.setChannel("direct");
          this.lastTo = target;
          return; // bare /r just arms the reply
        }
        ch = "direct";
        to = target;
        text = rest;
        this.lastTo = target;
      } else if (cmd === "/w" || cmd === "/whisper") {
        // Single-token target for now — quoted multi-word names come with the
        // friends phase, where whispers actually route.
        const gap = rest.indexOf(" ");
        if (gap === -1) {
          this.note("USAGE: /W NAME MESSAGE");
          return;
        }
        ch = "direct";
        to = rest.slice(0, gap);
        text = rest.slice(gap + 1).trim();
        if (text.length === 0) {
          this.note("USAGE: /W NAME MESSAGE");
          return;
        }
        this.lastTo = to;
      } else {
        this.note("UNKNOWN COMMAND");
        return;
      }
    } else if (ch === "direct") {
      if (!this.lastTo) {
        this.note("USAGE: /W NAME MESSAGE");
        return;
      }
      to = this.lastTo;
    }
    this.handlers.onSend({ ch, text, ...(to ? { to } : {}) });
    this.field.value = "";
    this.closeInput(); // classic MMO flow: Enter types, Enter sends + releases
  }

  /** Full opacity now; fade to a whisper after idle (unless focused). */
  private wake(): void {
    this.logEl.style.opacity = "0.94";
    if (this.fadeTimer !== null) clearTimeout(this.fadeTimer);
    this.fadeTimer = window.setTimeout(() => {
      if (!this.focused) this.logEl.style.opacity = "0.2";
    }, IDLE_FADE_MS);
  }
}
